import type { NormalizedPoint, NormalizedRect } from '../types/geometry.js';
import { clamp01, distance, midpoint, rectFromPoints, roundTo } from '../types/geometry.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import type { FaceObservation } from '../types/observations.js';
import {
  EYE_A_CONTOUR,
  EYE_A_VERTICAL,
  EYE_B_CONTOUR,
  EYE_B_VERTICAL,
  LANDMARKS,
  MIN_LANDMARK_COUNT,
  centroid,
  eyeAspectRatio,
  eyeCenters,
  meshBox,
  pick,
} from './landmarks.js';
import {
  DEFAULT_HEAD_TOP_OPTIONS,
  estimateHeadTop,
  mergeSilhouetteEstimate,
  type HeadTopEstimatorOptions,
  type SilhouetteRefineResult,
} from './headTop.js';
import type { VisibleChinEstimate } from './chinContour.js';
import { combinePose, geometricPose, matrixToPose } from './pose.js';

export interface FaceGeometryOptions {
  headTop?: HeadTopEstimatorOptions;
  /**
   * Size of the original image the landmarks came from. All measurements stay
   * normalized; this is carried into the metrics so pixel values can be audited.
   * The browser pipeline always passes the real size.
   */
  imageSize?: { width: number; height: number };
  /** Refined head top from the silhouette scan, when pixels were available. */
  silhouette?: SilhouetteRefineResult;
  /** Blend factor for the horizontal centre, 0 = eye line, 1 = head box. */
  centerFromHeadBoxWeight?: number;
  /** Diagnostic-only visible chin estimate; never used for a measurement. */
  visibleChin?: VisibleChinEstimate;
}

export interface FaceGeometryResult {
  metrics: FaceGeometryMetrics | null;
  /** True when the landmark set was too incomplete to measure anything. */
  incomplete: boolean;
  reason?: string;
}

/** Orders two optional points left-to-right in image coordinates. */
const orderByX = (
  a: NormalizedPoint | null,
  b: NormalizedPoint | null,
): { left: NormalizedPoint; right: NormalizedPoint } | null => {
  if (!a || !b) return null;
  return a.x <= b.x ? { left: a, right: b } : { left: b, right: a };
};

const EYE_DISTANCE_MIN = 0.04;

/**
 * Turns raw landmarks into every normalized measurement the rule engine needs.
 *
 * Nothing here knows about documents: the output is a document-independent
 * description of where the head, eyes, nose and chin are.
 */
export const computeFaceGeometry = (
  observation: FaceObservation,
  options: FaceGeometryOptions = {},
): FaceGeometryResult => {
  const landmarks = observation.landmarks;
  if (!Array.isArray(landmarks) || landmarks.length < MIN_LANDMARK_COUNT) {
    return {
      metrics: null,
      incomplete: true,
      reason: `Недостаточно landmarks (${landmarks?.length ?? 0}); требуется ${MIN_LANDMARK_COUNT}.`,
    };
  }

  const eyes = eyeCenters(landmarks);
  const noseTip = pick(landmarks, LANDMARKS.noseTip) ?? centroid(landmarks, [1, 2, 4]);
  const chin = pick(landmarks, LANDMARKS.chinBottom);
  const forehead = pick(landmarks, LANDMARKS.foreheadTopCenter);
  const mouthCenter = centroid(landmarks, [LANDMARKS.upperLipInner, LANDMARKS.lowerLipInner]);
  const box = meshBox(landmarks);

  if (!eyes || !noseTip || !chin || !forehead || !mouthCenter || !box) {
    return {
      metrics: null,
      incomplete: true,
      reason: 'Ключевые landmarks (глаза, нос, подбородок, лоб) не найдены.',
    };
  }

  // Derive left/right from image coordinates so a mirrored model cannot swap them.
  const eyeLeft = eyes.eyeACenter.x <= eyes.eyeBCenter.x ? eyes.eyeACenter : eyes.eyeBCenter;
  const eyeRight = eyes.eyeACenter.x <= eyes.eyeBCenter.x ? eyes.eyeBCenter : eyes.eyeACenter;
  const leftIsA = eyes.eyeACenter.x <= eyes.eyeBCenter.x;
  const eyeCenter = midpoint(eyeLeft, eyeRight);
  const eyeLineY = eyeCenter.y;
  const eyeSpan = distance(eyeLeft, eyeRight);

  const earLeft = eyeAspectRatio(
    landmarks,
    leftIsA ? [EYE_A_CONTOUR[0], EYE_A_CONTOUR[3]] : [EYE_B_CONTOUR[0], EYE_B_CONTOUR[3]],
    leftIsA ? EYE_A_VERTICAL : EYE_B_VERTICAL,
  );
  const earRight = eyeAspectRatio(
    landmarks,
    leftIsA ? [EYE_B_CONTOUR[0], EYE_B_CONTOUR[3]] : [EYE_A_CONTOUR[0], EYE_A_CONTOUR[3]],
    leftIsA ? EYE_B_VERTICAL : EYE_A_VERTICAL,
  );
  const earCandidates = [earLeft, earRight].filter((value): value is number => value !== null);
  const earMin = earCandidates.length > 0 ? Math.min(...earCandidates) : 0;

  const headTopEstimate = mergeSilhouetteEstimate(
    estimateHeadTop(landmarks, eyeLineY, options.headTop ?? DEFAULT_HEAD_TOP_OPTIONS),
    options.silhouette ?? {
      found: false,
      headTopY: 0,
      confidence: 0,
      contrast: 0,
      columnsUsed: 0,
      reachedImageTop: false,
      notes: [],
    },
    { meshTopY: forehead.y },
  );

  // Temple-to-temple width is the closest landmark-based proxy for head width.
  const templePair = orderByX(
    pick(landmarks, LANDMARKS.leftTemple),
    pick(landmarks, LANDMARKS.rightTemple),
  );
  const cheekPair = orderByX(
    pick(landmarks, LANDMARKS.leftCheekEdge),
    pick(landmarks, LANDMARKS.rightCheekEdge),
  );
  const headWidth =
    templePair !== null
      ? Math.abs(templePair.right.x - templePair.left.x)
      : cheekPair !== null
        ? Math.abs(cheekPair.right.x - cheekPair.left.x)
        : box.width;

  const headTopY = headTopEstimate.source === 'unavailable' ? forehead.y : headTopEstimate.headTopY;
  const headHeight = chin.y - headTopY;
  const faceHeight = chin.y - forehead.y;

  const headBox: NormalizedRect =
    templePair && cheekPair
      ? rectFromPoints([
          { x: Math.min(templePair.left.x, cheekPair.left.x), y: headTopY },
          { x: Math.max(templePair.right.x, cheekPair.right.x), y: chin.y },
        ])
      : { x: box.x, y: headTopY, width: box.width, height: headHeight };

  const headBoxWeight = clamp01(options.centerFromHeadBoxWeight ?? 0.5);
  const faceCenterX =
    eyeCenter.x * (1 - headBoxWeight) + (headBox.x + headBox.width / 2) * headBoxWeight;
  const faceCenterY = headBox.y + headBox.height / 2;

  const landmarkQuality = clamp01(
    0.4 * observation.confidence +
      0.35 * clamp01(eyeSpan / EYE_DISTANCE_MIN) * 0.5 +
      0.25 * (headTopEstimate.confidence.value > 0 ? 1 : 0.4),
  );

  const geometric = geometricPose({
    landmarks,
    eyeLeft,
    eyeRight,
    eyeLineY,
    noseTip,
    landmarkQuality,
    // Normalized eye coordinates carry different x/y pixel scales.
    aspect: (options.imageSize?.width ?? 1) / (options.imageSize?.height ?? 1),
  });
  const pose = combinePose(geometric, matrixOrNull(observation));

  const margins = {
    top: clamp01(headTopY),
    bottom: clamp01(1 - chin.y),
    left: clamp01(headBox.x),
    right: clamp01(1 - (headBox.x + headBox.width)),
  };

  const metrics: FaceGeometryMetrics = {
    imageSize: options.imageSize ?? { width: 1, height: 1 },
    faceCount: 1,
    faceBox: box,
    headBox,
    faceBoxConfidence: {
      value: roundTo(clamp01(observation.confidence), 2),
    },
    chinPoint: chin,
    foreheadPoint: forehead,
    headTopPoint: { x: faceCenterX, y: headTopY },
    headTopConfidence: headTopEstimate.confidence,
    headTopSource: headTopEstimate.source,
    headTopNotes: headTopEstimate.notes,
    headTopEstimators: headTopEstimate.estimators,
    headTopBounds: headTopEstimate.bounds,
    headTopSilhouette: headTopEstimate.silhouette,
    faceHeight: roundTo(faceHeight, 4),
    headHeight: roundTo(headHeight, 4),
    headHeightRatio: roundTo(headHeight, 4),
    headWidth: roundTo(headWidth, 4),
    headAspect: roundTo(headWidth > 0 ? headHeight / headWidth : 0, 3),
    eyeLeft,
    eyeRight,
    eyeCenter,
    eyeLineFromTop: roundTo(eyeLineY, 4),
    eyeLineFromBottom: roundTo(1 - eyeLineY, 4),
    eyeDistance: roundTo(eyeSpan, 4),
    eyeAspectRatio: {
      left: roundTo(earLeft ?? 0, 3),
      right: roundTo(earRight ?? 0, 3),
      min: roundTo(earMin, 3),
    },
    eyeOpenSource: observation.blendshapes ? 'blendshapes' : 'geometric',
    eyeCentersSource: eyes.source,
    noseTip,
    mouthCenter,
    faceCenterX: roundTo(faceCenterX, 4),
    faceCenterY: roundTo(faceCenterY, 4),
    horizontalCenterOffset: roundTo(faceCenterX - 0.5, 4),
    verticalCenterOffset: roundTo(faceCenterY - 0.5, 4),
    roll: roundTo(pose.roll, 2),
    yaw: roundTo(pose.yaw, 2),
    pitch: roundTo(pose.pitch, 2),
    ...(options.visibleChin ? { visibleChin: options.visibleChin } : {}),
    poseSource: pose.source,
    poseConfidence: pose.confidence,
    margins,
    landmarks,
    usedLandmarkIndices: {
      foreheadTopCenter: LANDMARKS.foreheadTopCenter,
      chinBottom: LANDMARKS.chinBottom,
      noseTip: LANDMARKS.noseTip,
      eyeAContour: EYE_A_CONTOUR[0],
      eyeBContour: EYE_B_CONTOUR[0],
      leftTemple: LANDMARKS.leftTemple,
      rightTemple: LANDMARKS.rightTemple,
    },
  };

  return { metrics, incomplete: false };
};

const matrixOrNull = (
  observation: FaceObservation,
): { yaw: number; pitch: number; roll: number } | null => {
  const matrix = observation.transformationMatrix;
  if (!matrix || matrix.length < 16) return null;
  return matrixToPose(matrix);
};
